/**
 * Request Queue Service
 * Queues requests when offline and processes them when back online
 */

interface QueuedRequest {
  id: string;
  type: 'edge-function' | 'mutation' | 'upload';
  functionName?: string;
  table?: string;
  operation?: 'insert' | 'update' | 'delete';
  body?: any;
  createdAt: number;
  retryCount: number;
  maxRetries: number;
}

class RequestQueueService {
  private storageKey = 'request_queue';
  private isProcessing = false;
  private listeners: Array<(queue: QueuedRequest[]) => void> = [];

  constructor() {
    // Listen for online events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.processQueue());
    }
  }

  private getQueue(): QueuedRequest[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private saveQueue(queue: QueuedRequest[]): void {
    localStorage.setItem(this.storageKey, JSON.stringify(queue));
    this.notifyListeners(queue);
  }

  private notifyListeners(queue: QueuedRequest[]): void {
    this.listeners.forEach(listener => listener(queue));
  }

  add(request: Omit<QueuedRequest, 'id' | 'createdAt' | 'retryCount' | 'maxRetries'>): string {
    const id = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const queuedRequest: QueuedRequest = {
      ...request,
      id,
      createdAt: Date.now(),
      retryCount: 0,
      maxRetries: 3,
    };

    const queue = this.getQueue();
    queue.push(queuedRequest);
    this.saveQueue(queue);

    return id;
  }

  remove(id: string): void {
    const queue = this.getQueue();
    const filtered = queue.filter(req => req.id !== id);
    this.saveQueue(filtered);
  }

  async processQueue(): Promise<void> {
    if (this.isProcessing || !navigator.onLine) return;

    this.isProcessing = true;
    const queue = this.getQueue();
    const remaining: QueuedRequest[] = [];

    for (const request of queue) {
      try {
        await this.processRequest(request);
      } catch (error) {
        if (request.retryCount < request.maxRetries) {
          remaining.push({
            ...request,
            retryCount: request.retryCount + 1,
          });
        }
        // If max retries exceeded, drop the request
      }
    }

    this.saveQueue(remaining);
    this.isProcessing = false;
  }

  private async processRequest(request: QueuedRequest): Promise<void> {
    // Import dynamically to avoid circular dependencies
    const { edgeFunctions } = await import('./edgeFunctions');
    const { getAuthenticatedClient } = await import('./client');

    switch (request.type) {
      case 'edge-function':
        if (request.functionName) {
          await edgeFunctions.invoke(request.functionName, { body: request.body });
        }
        break;
      case 'mutation':
        if (request.table && request.operation) {
          const client = getAuthenticatedClient();
          const query = client.from(request.table as any);
          
          switch (request.operation) {
            case 'insert':
              await query.insert(request.body);
              break;
            case 'update':
              await query.update(request.body).match(request.body.id ? { id: request.body.id } : {});
              break;
            case 'delete':
              await query.delete().match(request.body);
              break;
          }
        }
        break;
    }
  }

  getQueueLength(): number {
    return this.getQueue().length;
  }

  getPendingRequests(): QueuedRequest[] {
    return this.getQueue();
  }

  subscribe(listener: (queue: QueuedRequest[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  clear(): void {
    this.saveQueue([]);
  }
}

export const requestQueue = new RequestQueueService();
